import { levelForPoints } from '@pacer/shared';
import { Avatar } from './ui';
import { Icon } from './Icon';

/**
 * A member's avatar wrapped in a progress ring that fills toward their next level, with the
 * level number tucked in the corner. Once someone maxes a cycle they prestige: the level resets
 * and a star with the prestige count rides on top. The at-a-glance "how am I doing" indicator.
 */
export function LevelBadge({
  name,
  color,
  photoUrl,
  points,
  size = 48,
}: {
  name: string;
  color: string;
  photoUrl?: string | null;
  points: number;
  size?: number;
}) {
  const { level, prestige, pct, atMax } = levelForPoints(points);
  const stroke = Math.max(2, Math.round(size * 0.06));
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;

  return (
    <span className="relative inline-flex shrink-0 items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="absolute inset-0 -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--color-border-subtle)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={circ * (1 - pct)}
          style={{ transition: 'stroke-dashoffset 500ms ease' }}
        />
      </svg>
      <Avatar name={name} color={color} photoUrl={photoUrl} size={size - stroke * 2 - 4} />

      {prestige > 0 && (
        <span
          className="tabular absolute -top-1 left-1/2 flex -translate-x-1/2 items-center gap-0.5 rounded-full px-1 font-mono text-[11px] font-medium leading-none"
          style={{ background: color, color: 'var(--color-surface)' }}
        >
          <Icon name="star" size={Math.max(8, Math.round(size * 0.17))} strokeWidth={2} />
          {prestige}
        </span>
      )}

      <span
        className="tabular absolute -bottom-1 left-1/2 -translate-x-1/2 rounded-full border px-1.5 font-mono text-[11px] font-medium"
        style={{
          background: atMax ? color : 'var(--color-surface)',
          borderColor: color,
          color: atMax ? 'var(--color-surface)' : color,
        }}
      >
        {level}
      </span>
    </span>
  );
}
